import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import './single.scss';

import Sidebar from '../../components/sidebar/Sidebar';
import Navbar from '../../components/navbar/Navbar';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
} from '@mui/material';

const Single = () => {
  const [merchant, setMerchant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openEdit, setOpenEdit] = useState(false);

  const [formData, setFormData] = useState({
    username: '',
    name: '',
    email: '',
    address: '',
    phone: '',
    ssmno: '',
    sstid: '',
    sstPercentage: '',
    serviceChargePercentage: '',
    password: '',
  });

  const getToken = () => {
    return (
      localStorage.getItem('accessToken') ||
      localStorage.getItem('token') ||
      ''
    );
  };

  const fetchMerchant = async () => {
    try {
      setLoading(true);
      const token = getToken();

      if (!token) {
        setError('No token found. Please login first.');
        setLoading(false);
        return;
      }

      const decoded = jwtDecode(token);
      const merchantId = decoded?.merchantId;

      if (!merchantId) {
        setError('Merchant ID not found in token.');
        setLoading(false);
        return;
      }

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/merchants/me`,
        {
          headers: {
            token: `Bearer ${token}`,
            merchantid: merchantId,
          },
        }
      );

      setMerchant(res.data);
      setFormData({
        username: res.data.username || '',
        name: res.data.name || '',
        email: res.data.email || '',
        address: res.data.address || '',
        phone: res.data.phone || '',
        ssmno: res.data.ssmno || '',
        sstid: res.data.sstid || '',
        sstPercentage: res.data.sstPercentage || '',
        serviceChargePercentage: res.data.serviceChargePercentage || '',
        password: '',
      });
      setError('');
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to fetch merchant data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMerchant();
  }, []);

  const handleOpenEdit = () => {
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdate = async () => {
    try {
      const token = getToken();

      if (!merchant?._id) return;

      const payload = { ...formData };

      if (!payload.password) {
        delete payload.password;
      }

      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/merchants/${merchant._id}`,
        payload,
        {
          headers: {
            token: `Bearer ${token}`,
          },
        }
      );

      setMerchant(res.data);
      handleCloseEdit();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Failed to update merchant');
    }
  };

  return (
    <div className="single">
      <Sidebar />
      <div className="singleContainer">
        <Navbar />

        <div className="profileWrapper">
          <div className="topBar">
            <h1>Merchant Profile</h1>
            {!loading && !error && merchant && (
              <Button variant="contained" onClick={handleOpenEdit}>
                Edit Profile
              </Button>
            )}
          </div>

          {loading && <div className="message">Loading...</div>}
          {error && <div className="message error">{error}</div>}

          {!loading && !error && merchant && (
            <>
              <div className="profileCard">
                <div className="sectionTitle">Basic Information</div>

                <div className="row"><span className="label">ID</span><span className="value">{merchant._id}</span></div>
                <div className="row"><span className="label">Username</span><span className="value">{merchant.username || '-'}</span></div>
                <div className="row"><span className="label">Business Name</span><span className="value">{merchant.name || '-'}</span></div>
                <div className="row"><span className="label">Email</span><span className="value">{merchant.email || '-'}</span></div>
                <div className="row"><span className="label">Phone</span><span className="value">{merchant.phone || '-'}</span></div>
                <div className="row"><span className="label">Address</span><span className="value">{merchant.address || '-'}</span></div>
              </div>

              <div className="profileCard">
                <div className="sectionTitle">Business Details</div>

                <div className="row"><span className="label">SSM No</span><span className="value">{merchant.ssmno || '-'}</span></div>
                <div className="row"><span className="label">SST ID</span><span className="value">{merchant.sstid || '-'}</span></div>
                <div className="row"><span className="label">SST %</span><span className="value">{merchant.sstPercentage ?? '-'}</span></div>
                <div className="row"><span className="label">Service Charge %</span><span className="value">{merchant.serviceChargePercentage ?? '-'}</span></div>
                <div className="row"><span className="label">Is Merchant</span><span className="value">{merchant.isMerchant ? 'Yes' : 'No'}</span></div>
                <div className="row"><span className="label">Is Admin</span><span className="value">{merchant.isAdmin ? 'Yes' : 'No'}</span></div>
                <div className="row"><span className="label">Is Active</span><span className="value">{merchant.isActive ? 'Yes' : 'No'}</span></div>
              </div>

              <div className="profileCard">
                <div className="sectionTitle">Documents</div>

                <div className="docGrid">
                  <div className="docBox">
                    <h3>IC Image</h3>
                    {merchant.icImage ? (
                      <>
                        <img src={merchant.icImage} alt="IC" />
                        <a href={merchant.icImage} target="_blank" rel="noreferrer">
                          View Full
                        </a>
                      </>
                    ) : (
                      <p>No IC uploaded</p>
                    )}
                  </div>

                  <div className="docBox">
                    <h3>SSM Image</h3>
                    {merchant.ssmImage ? (
                      <>
                        <img src={merchant.ssmImage} alt="SSM" />
                        <a href={merchant.ssmImage} target="_blank" rel="noreferrer">
                          View Full
                        </a>
                      </>
                    ) : (
                      <p>No SSM uploaded</p>
                    )}
                  </div>

                  <div className="docBox">
                    <h3>Bank Statement</h3>
                    {merchant.bankstatement ? (
                      <>
                        <div className="fileBox">PDF / Document</div>
                        <a href={merchant.bankstatement} target="_blank" rel="noreferrer">
                          Open Bank Statement
                        </a>
                      </>
                    ) : (
                      <p>No bank statement uploaded</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="profileCard">
                <div className="sectionTitle">System Info</div>

                <div className="row">
                  <span className="label">Daily Counter Date</span>
                  <span className="value">{merchant.dailyOrderCounter?.date || '-'}</span>
                </div>
                <div className="row">
                  <span className="label">Daily Counter Seq</span>
                  <span className="value">{merchant.dailyOrderCounter?.seq ?? 0}</span>
                </div>
                <div className="row">
                  <span className="label">Created At</span>
                  <span className="value">
                    {merchant.createdAt ? new Date(merchant.createdAt).toLocaleString() : '-'}
                  </span>
                </div>
                <div className="row">
                  <span className="label">Updated At</span>
                  <span className="value">
                    {merchant.updatedAt ? new Date(merchant.updatedAt).toLocaleString() : '-'}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={openEdit} onClose={handleCloseEdit} fullWidth maxWidth="md">
        <DialogTitle>Edit Merchant Profile</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Business Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SSM No"
                name="ssmno"
                value={formData.ssmno}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SST ID"
                name="sstid"
                value={formData.sstid}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SST Percentage"
                name="sstPercentage"
                value={formData.sstPercentage}
                onChange={handleChange}
                type="number"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Service Charge Percentage"
                name="serviceChargePercentage"
                value={formData.serviceChargePercentage}
                onChange={handleChange}
                type="number"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="New Password (optional)"
                name="password"
                value={formData.password}
                onChange={handleChange}
                type="password"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Single;